import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserProfile, HomelabStatus, AuthUser, AuthSession, AppLayer, TriggeredAlertPayload } from '../types';
import {
  getServerUrl,
  fetchStatus,
   checkServerVersion,
  ServerVersionInfo,
  getApkDownloadUrl,
  getStoredToken,
  getStoredAuthUser,
  setStoredToken,
  setStoredAuthUser,
  setStoredProfile,
  hydrateSecureSession,
  checkAuthMe,
  logoutUser
} from '../services/api';
import { CURRENT_APP_VERSION, AppVersionManifest } from '../version';
import { startPurr, stopPurr, isPurrActive, triggerHaptic, adaptPurrToBpm } from '../services/purrSynthesizer';
import { bioTelemetry } from '../services/bioTelemetry';
import {
  triggerInAppOtaUpdate,
  openUnknownAppSettings,
  getOtaDownloadState,
  installDownloadedApk
} from '../services/otaUpdater';
import { connectionManager, NetworkRoute, RouteStatus } from '../services/connectionManager';
import { notificationService } from '../services/notificationService';
import {
  EdgeExecutionMode,
  EdgeModelMeta,
  EdgeProbeResult,
  getEdgeExecutionMode,
  setEdgeExecutionMode,
  getSelectedEdgeModel,
  setSelectedEdgeModel,
  verifyLocalGemmaModel,
  AVAILABLE_EDGE_MODELS
} from '../services/edgeModelAdapter';

export interface OtaState {
  isUpdating: boolean;
  status: 'idle' | 'downloading' | 'preparing' | 'permission_required' | 'ready' | 'error';
  percent: number;
  bytesDownloaded: number;
  totalBytes: number;
  error?: string;
}

const DEFAULT_FAMILY_USER: AuthUser = {
  userId: 'lycan',
  displayName: 'Lycan',
  role: 'family',
  title: 'Household Guardian 🌸'
};

interface AppContextType {
  // Identity & Sovereign Authentication
  authUser: AuthUser | null;
  authToken: string | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  authModalOpen: boolean;
  setAuthModalOpen: (open: boolean) => void;
  loginSession: (session: AuthSession) => void;
  logout: () => Promise<void>;
  setGuestMode: () => void;

  profile: UserProfile;
  setProfile: (p: UserProfile) => void;
  serverUrl: string;
  setServerUrl: (url: string) => void;
  isOnline: boolean;
  latencyMs: number;
  status: HomelabStatus | null;
  activeLayer: AppLayer;
  setActiveLayer: (layer: AppLayer) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isPurring: boolean;
  togglePurr: () => void;
  liveBpm: number | null;
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  notificationModalOpen: boolean;
  setNotificationModalOpen: (open: boolean) => void;
  bioModalOpen: boolean;
  setBioModalOpen: (open: boolean) => void;
  allDecksModalOpen: boolean;
  setAllDecksModalOpen: (open: boolean) => void;
  refreshStatus: () => Promise<void>;
  networkMode: NetworkRoute;
  setNetworkPreset: (mode: 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM', customUrl?: string) => void;
  recheckNetworkRoute: () => Promise<void>;

  // Sovereign OTA Update System
  currentVersion: AppVersionManifest;
  serverVersion: ServerVersionInfo | null;
  isUpdateAvailable: boolean;
  updateDismissed: boolean;
  setUpdateDismissed: (dismissed: boolean) => void;
  checkForUpdates: () => Promise<void>;
  installUpdate: () => Promise<void>;
  otaState: OtaState;
  openOtaSettings: () => Promise<void>;
  resetOtaState: () => void;

  // On-Device Local Edge Model Engine
  edgeMode: EdgeExecutionMode;
  setEdgeMode: (mode: EdgeExecutionMode) => void;
  edgeModel: EdgeModelMeta;
  setEdgeModel: (modelId: string) => void;
  edgeProbe: EdgeProbeResult | null;
  recheckEdgeModel: (force?: boolean) => Promise<EdgeProbeResult>;
  availableEdgeModels: EdgeModelMeta[];

  // Perimeter Cameras & Triggered Alert Deep-Linking
  selectedCameraId: string | null;
  setSelectedCameraId: (id: string | null) => void;
  activeEventId: string | null;
  setActiveEventId: (id: string | null) => void;
  activeAlertPayload: TriggeredAlertPayload | null;
  setActiveAlertPayload: (alert: TriggeredAlertPayload | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initial Auth State from Storage (defaulting to Family Guardian if not yet signed in)
  const [authToken, setAuthTokenState] = useState<string | null>(() => getStoredToken());
  const [authUser, setAuthUserState] = useState<AuthUser | null>(() => getStoredAuthUser() || DEFAULT_FAMILY_USER);
  const [authModalOpen, setAuthModalOpen] = useState<boolean>(false);

  const [profile, setProfileState] = useState<UserProfile>(() => {
    const cachedUser = getStoredAuthUser();
    if (cachedUser?.userId) {
      return (cachedUser.userId as UserProfile) || 'lycan';
    }
    const stored = localStorage.getItem('fissal_profile') as string;
    if (stored && ['echo', 'lycan', 'mom', 'onyx', 'trey', 'family'].includes(stored)) {
      return stored as UserProfile;
    }
    return 'lycan';
  });

  const [serverUrl, setServerUrlState] = useState<string>(getServerUrl);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [latencyMs, setLatencyMs] = useState<number>(18);
  const [status, setStatus] = useState<HomelabStatus | null>(null);

  const [activeLayer, setActiveLayerState] = useState<AppLayer>(() => {
    const saved = localStorage.getItem('fissal_active_layer');
    if (saved === 'devops' || saved === 'family') return saved;
    return 'family';
  });

  const [activeTab, setActiveTab] = useState<string>(() => {
    const savedLayer = localStorage.getItem('fissal_active_layer');
    if (savedLayer === 'devops') return 'cockpit';
    return 'cameras';
  });

  const setActiveLayer = useCallback((layer: AppLayer) => {
    setActiveLayerState(layer);
    localStorage.setItem('fissal_active_layer', layer);
    if (layer === 'devops') {
      setActiveTab((prev) => (['cockpit', 'diagnostics', 'terminal', 'fissal', 'devices', 'prime', 'smarthome', 'stagerig'].includes(prev) ? prev : 'cockpit'));
    } else {
      setActiveTab((prev) => (['cameras', 'fissal', 'living', 'photos', 'lights', 'note', 'devices'].includes(prev) ? prev : 'cameras'));
    }
  }, []);

  const [isPurringState, setIsPurringState] = useState<boolean>(false);
  const [liveBpm, setLiveBpm] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState<boolean>(false);
  const [bioModalOpen, setBioModalOpen] = useState<boolean>(false);
  const [allDecksModalOpen, setAllDecksModalOpen] = useState<boolean>(false);

  // OTA Update State
  const [serverVersion, setServerVersion] = useState<ServerVersionInfo | null>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false);
  const [updateDismissed, setUpdateDismissed] = useState<boolean>(false);
  const [otaState, setOtaState] = useState<OtaState>({
    isUpdating: false,
    status: 'idle',
    percent: 0,
    bytesDownloaded: 0,
    totalBytes: 0
  });

  // On-Device Local Edge Model Engine State
  const [edgeMode, setEdgeModeState] = useState<EdgeExecutionMode>(getEdgeExecutionMode);
  const [edgeModel, setEdgeModelState] = useState<EdgeModelMeta>(getSelectedEdgeModel);
  const [edgeProbe, setEdgeProbe] = useState<EdgeProbeResult | null>(null);

  const recheckEdgeModel = useCallback(async (force: boolean = false) => {
    const probe = await verifyLocalGemmaModel(force);
    setEdgeProbe(probe);
    return probe;
  }, []);

  const setEdgeMode = useCallback((mode: EdgeExecutionMode) => {
    setEdgeExecutionMode(mode);
    setEdgeModeState(mode);
  }, []);

  const setEdgeModel = useCallback((modelId: string) => {
    setSelectedEdgeModel(modelId);
    setEdgeModelState(getSelectedEdgeModel());
    recheckEdgeModel(true);
  }, [recheckEdgeModel]);

  useEffect(() => {
    recheckEdgeModel();
  }, [recheckEdgeModel]);

  // Perimeter Camera Deep-Linking from Native Notifications
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [activeAlertPayload, setActiveAlertPayload] = useState<TriggeredAlertPayload | null>(null);

  useEffect(() => {
    const handleNotificationClicked = (e: any) => {
      const detail = e.detail;
      console.log('🐾 [AppContext] Notification click received:', detail);
      const data = detail?.data || detail || {};
      const cam = detail?.camera || data?.camera || null;
      const evtId = data?.eventId || data?.id || detail?.id || null;

      if (cam) {
        setSelectedCameraId(cam);
      }
      if (evtId) {
        setActiveEventId(evtId);
      }
      if (cam || evtId || detail?.topic === 'camera_detections') {
        const snapshotUrl = data?.snapshotUrl || data?.imageUrl || (evtId ? `/api/frigate/snapshot/${evtId}` : '');
        const clipUrl = data?.clipUrl || (evtId ? `/api/frigate/clip/${evtId}.mp4` : undefined);
        setActiveAlertPayload({
          id: evtId || `alert_${Date.now()}`,
          eventId: evtId || undefined,
          title: detail?.title || data?.title || `${cam || 'Perimeter'}: Detection Alert`,
          message: detail?.body || data?.summary || data?.message || 'Perimeter camera detection triggered.',
          summary: data?.summary || detail?.body || '',
          camera: cam || 'ipcam_100',
          label: data?.label || 'motion',
          sub_label: data?.sub_label || null,
          zones: Array.isArray(data?.zones) ? data.zones : [],
          confidence: data?.confidence || null,
          timestamp: data?.startTime || data?.timestamp || Date.now(),
          snapshotUrl,
          clipUrl
        });
        setActiveLayerState('family');
        localStorage.setItem('fissal_active_layer', 'family');
        setActiveTab('cameras');
        triggerHaptic([40, 60]);
      }
    };

    window.addEventListener('fissal:notification_clicked', handleNotificationClicked);

    // Check for cold-start pending notification
    const pending = (window as any).__FISSAL_PENDING_NOTIFICATION__;
    if (pending) {
      console.log('🐾 [AppContext] Processing pending cold-start notification:', pending);
      handleNotificationClicked({ detail: pending });
      (window as any).__FISSAL_PENDING_NOTIFICATION__ = null;
    }

    return () => {
      window.removeEventListener('fissal:notification_clicked', handleNotificationClicked);
    };
  }, []);

  const isAuthenticated = Boolean(authUser);
  const isGuest = false;

  // Verify stored session on boot with native hydration & failover tolerance
  useEffect(() => {
    const verifyInitialSession = async () => {
      const hydrated = await hydrateSecureSession();
      const currentToken = hydrated.token || getStoredToken();

      if (hydrated.token && !authToken) {
        setAuthTokenState(hydrated.token);
      }
      if (hydrated.user) {
        setAuthUserState(hydrated.user);
      }
      if (hydrated.profile && ['echo', 'lycan', 'mom', 'onyx', 'trey', 'family'].includes(hydrated.profile)) {
        setProfileState(hydrated.profile as UserProfile);
      }

      if (currentToken) {
        const result = await checkAuthMe();
        if (result.ok && result.user) {
          setAuthUserState(result.user);
          setProfileState((result.user.userId as UserProfile) || 'echo');
        } else if (result.expired) {
          console.warn('🐾 [AppContext] Token expired; reverting to Family Guardian mode.');
          setAuthTokenState(null);
          setStoredToken(null);
          setAuthUserState(DEFAULT_FAMILY_USER);
          setProfileState('lycan');
        }
      }

      notificationService.initChannels().catch(() => {});
    };
    verifyInitialSession();
  }, []);

  // Real-time Push Notification WebSocket Client
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: any = null;
    let isDestroyed = false;

    const connectWs = () => {
      if (isDestroyed) return;
      try {
        const wsUrl = connectionManager.getWsUrl();
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('🐾 [AppContext] Notification WebSocket connected:', wsUrl);
          const token = getStoredToken();
          const user = getStoredAuthUser() || DEFAULT_FAMILY_USER;
          if (token) {
            ws?.send(JSON.stringify({ type: 'auth', token, userId: user?.userId || 'echo' }));
          } else {
            ws?.send(JSON.stringify({ type: 'auth', userId: user?.userId || 'lycan' }));
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'push_notification' && data.notification) {
              console.log('🐾 [AppContext] Incoming push notification from gateway:', data.notification.title);
              notificationService.handleIncomingNotification(data.notification);
              window.dispatchEvent(new CustomEvent('fissal:live_alert', { detail: data.notification }));
            } else if (data.type === 'camera_alert' && data.alert) {
              window.dispatchEvent(new CustomEvent('fissal:live_alert', { detail: data.alert }));
            } else if (data.type === 'stage_event' && data.data) {
              window.dispatchEvent(new CustomEvent('stage_event', { detail: data.data }));
            }
          } catch (err) {
            console.warn('[AppContext] WS message parse error:', err);
          }
        };

        ws.onclose = () => {
          if (!isDestroyed) {
            reconnectTimer = setTimeout(connectWs, 5000);
          }
        };

        ws.onerror = () => {
          ws?.close();
        };
      } catch {
        if (!isDestroyed) {
          reconnectTimer = setTimeout(connectWs, 5000);
        }
      }
    };

    connectWs();

    let activeWsUrl = connectionManager.getWsUrl();

    const unsubRoute = connectionManager.subscribe((routeStatus) => {
      const newWsUrl = connectionManager.getWsUrl();
      if (routeStatus.isOnline && newWsUrl !== activeWsUrl) {
        activeWsUrl = newWsUrl;
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          ws.close();
          reconnectTimer = setTimeout(connectWs, 250);
        }
      }
    });

    return () => {
      isDestroyed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      unsubRoute();
      if (ws) ws.close();
    };
  }, [authToken]);

  const loginSession = (session: AuthSession) => {
    setAuthTokenState(session.token);
    setAuthUserState(session.user);
    setStoredToken(session.token);
    setStoredAuthUser(session.user);

    const targetProfile = (session.user.userId as UserProfile) || 'echo';
    setProfileState(targetProfile);
    setStoredProfile(targetProfile);

    // Prioritize Family Cameras on Family login, or preserve active tab
    if (session.user.role === 'family') {
      setActiveLayer('family');
      setActiveTab('cameras');
    }

    setAuthModalOpen(false);
  };

  const logout = async () => {
    await logoutUser();
    setAuthTokenState(null);
    setAuthUserState(DEFAULT_FAMILY_USER);
    setProfileState('lycan');
    setStoredProfile('lycan');
    setActiveLayer('family');
    setActiveTab('cameras');
  };

  const setGuestMode = () => {
    setAuthUserState(DEFAULT_FAMILY_USER);
    setProfileState('lycan');
    setAuthModalOpen(false);
  };

  const setProfile = (newProfile: UserProfile) => {
    setProfileState(newProfile);
    setStoredProfile(newProfile);
  };

  const setServerUrl = (url: string) => {
    connectionManager.setPresetMode('CUSTOM', url);
  };

  const setNetworkPreset = (mode: 'AUTO' | 'LAN' | 'TAILSCALE' | 'PUBLIC' | 'CUSTOM', customUrl?: string) => {
    connectionManager.setPresetMode(mode, customUrl);
  };

  const recheckNetworkRoute = useCallback(async () => {
    await connectionManager.resolveFastestRoute();
  }, []);

  const networkMode: NetworkRoute = connectionManager.getActiveRoute();

  // Dynamic connection manager subscription with background session re-verification
  useEffect(() => {
    let lastUrl = connectionManager.getActiveUrl();
    let wasOnline = false;

    const unsubscribe = connectionManager.subscribe(async (routeStatus: RouteStatus) => {
      setServerUrlState(routeStatus.url);
      setLatencyMs(routeStatus.latencyMs);
      setIsOnline(routeStatus.isOnline);

      if (routeStatus.isOnline && (!wasOnline || routeStatus.url !== lastUrl)) {
        wasOnline = true;
        lastUrl = routeStatus.url;
        const currentToken = getStoredToken();
        if (currentToken) {
          try {
            const check = await checkAuthMe();
            if (check.ok && check.user) {
              setAuthUserState(check.user);
            }
          } catch {}
        }
      } else if (!routeStatus.isOnline) {
        wasOnline = false;
      }
    });
    return unsubscribe;
  }, []);

  const refreshStatus = useCallback(async () => {
    const start = performance.now();
    try {
      const data = await fetchStatus();
      setLatencyMs(Math.round(performance.now() - start));
      setStatus(data);
      setIsOnline(true);
    } catch {
      setIsOnline(false);
    }
  }, []);

  // CPU & Battery Efficiency: only poll server status while app is visible in foreground
  useEffect(() => {
    refreshStatus();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshStatus();
      }
    }, 15000);
    const onVisChange = () => {
      if (document.visibilityState === 'visible') {
        refreshStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [refreshStatus]);

  const checkForUpdates = useCallback(async () => {
    try {
      const info = await checkServerVersion();
      if (!info) return;
      setServerVersion(info);

      const hasNewerTimestamp = Boolean(
        info.buildTimestamp &&
        CURRENT_APP_VERSION.buildTimestamp &&
        info.buildTimestamp > CURRENT_APP_VERSION.buildTimestamp
      );
      const hasDifferentVersion = info.version !== CURRENT_APP_VERSION.version;

      const isNewer = Boolean(info.apkAvailable && (hasNewerTimestamp || hasDifferentVersion));
      setIsUpdateAvailable(isNewer);
    } catch (err) {
      console.warn('[OTA] Check for updates failed:', err);
    }
  }, []);

  const openOtaSettings = useCallback(async () => {
    await openUnknownAppSettings();
  }, []);

  const resetOtaState = useCallback(() => {
    setOtaState({
      isUpdating: false,
      status: 'idle',
      percent: 0,
      bytesDownloaded: 0,
      totalBytes: 0
    });
  }, []);

  const syncNativeDownloadState = useCallback(async () => {
    const nativeState = await getOtaDownloadState();
    if (!nativeState) return;

    if (nativeState.isReady || nativeState.status === 'ready') {
      setOtaState({
        isUpdating: false,
        status: 'ready',
        percent: 100,
        bytesDownloaded: nativeState.bytesDownloaded,
        totalBytes: nativeState.totalBytes
      });
    } else if (nativeState.isDownloading || nativeState.status === 'downloading') {
      setOtaState({
        isUpdating: true,
        status: 'downloading',
        percent: nativeState.percent,
        bytesDownloaded: nativeState.bytesDownloaded,
        totalBytes: nativeState.totalBytes
      });
    } else if (nativeState.status === 'error' && nativeState.error) {
      setOtaState((prev) => ({
        ...prev,
        isUpdating: false,
        status: 'error',
        error: nativeState.error || 'Download failed.'
      }));
    }
  }, []);

  const installUpdate = useCallback(async () => {
    if (otaState.status === 'ready') {
      triggerHaptic([40, 80, 40]);
      const res = await installDownloadedApk();
      if (!res.ok && res.status === 'permission_required') {
        setOtaState((prev) => ({
          ...prev,
          isUpdating: false,
          status: 'permission_required',
          error: 'Permission needed: Please allow Fissal Prime to install unknown apps.'
        }));
      } else if (!res.ok) {
        setOtaState((prev) => ({
          ...prev,
          isUpdating: false,
          status: 'error',
          error: res.error || 'Failed to launch installer.'
        }));
      }
      return;
    }

    const downloadUrl = getApkDownloadUrl();
    triggerHaptic([40, 80, 40]);

    setOtaState((prev) => ({
      isUpdating: true,
      status: 'downloading',
      percent: prev.percent > 0 ? prev.percent : 0,
      bytesDownloaded: prev.bytesDownloaded > 0 ? prev.bytesDownloaded : 0,
      totalBytes: serverVersion?.apkSizeBytes || 5500000
    }));

    const result = await triggerInAppOtaUpdate(downloadUrl, (progress) => {
      setOtaState((prev) => ({
        ...prev,
        isUpdating: progress.status !== 'error' && progress.status !== 'ready',
        status: progress.status,
        percent: progress.percent !== undefined ? progress.percent : prev.percent,
        bytesDownloaded: progress.bytesDownloaded || prev.bytesDownloaded,
        totalBytes: progress.totalBytes || prev.totalBytes,
        error: progress.error
      }));
    });

    if (!result.ok && result.status === 'permission_required') {
      setOtaState((prev) => ({
        ...prev,
        isUpdating: false,
        status: 'permission_required',
        error: 'Permission needed: Please allow Fissal Prime to install unknown apps.'
      }));
    } else if (!result.ok) {
      setOtaState((prev) => ({
        ...prev,
        isUpdating: false,
        status: 'error',
        error: result.error || 'Failed to download update.'
      }));
    }
  }, [serverVersion, otaState.status]);

  useEffect(() => {
    checkForUpdates();
    syncNativeDownloadState();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
        syncNativeDownloadState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const updateInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    }, 120000);
    return () => {
      clearInterval(updateInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkForUpdates, syncNativeDownloadState]);

  // CPU Efficiency: Only poll bio-telemetry when Purr Engine or BioSyncModal is active
  useEffect(() => {
    if (!isPurringState && !bioModalOpen) return;
    const handleHeartRate = (bpm: number | null) => {
      setLiveBpm(bpm);
      if (bpm && bpm > 0 && isPurrActive()) {
        adaptPurrToBpm(bpm);
      }
    };
    bioTelemetry.startSync(handleHeartRate, 10000);
    return () => {
      bioTelemetry.stopSync(handleHeartRate);
    };
  }, [isPurringState, bioModalOpen]);

  const togglePurr = () => {
    if (isPurrActive()) {
      stopPurr();
      setIsPurringState(false);
      triggerHaptic(20);
    } else {
      startPurr(status?.persona?.vitals?.purrHz || 42.8);
      setIsPurringState(true);
      if (liveBpm && liveBpm > 0) {
        adaptPurrToBpm(liveBpm);
      }
      bioTelemetry.fetchHeartRate().then((res) => {
        if (res.bpm && res.bpm > 0) {
          setLiveBpm(res.bpm);
          adaptPurrToBpm(res.bpm);
        }
      });
      triggerHaptic([30, 40, 30]);
    }
  };

  return (
    <AppContext.Provider
      value={{
        authUser,
        authToken,
        isAuthenticated,
        isGuest,
        authModalOpen,
        setAuthModalOpen,
        loginSession,
        logout,
        setGuestMode,
        profile,
        setProfile,
        serverUrl,
        setServerUrl,
        isOnline,
        latencyMs,
        status,
        activeLayer,
        setActiveLayer,
        activeTab,
        setActiveTab,
        isPurring: isPurringState,
        togglePurr,
        liveBpm,
        settingsOpen,
        setSettingsOpen,
        notificationModalOpen,
        setNotificationModalOpen,
        bioModalOpen,
        setBioModalOpen,
        allDecksModalOpen,
        setAllDecksModalOpen,
        refreshStatus,
        networkMode,
        setNetworkPreset,
        recheckNetworkRoute,
        currentVersion: CURRENT_APP_VERSION,
        serverVersion,
        isUpdateAvailable,
        updateDismissed,
        setUpdateDismissed,
        checkForUpdates,
        installUpdate,
        otaState,
        openOtaSettings,
        resetOtaState,
        edgeMode,
        setEdgeMode,
        edgeModel,
        setEdgeModel,
        edgeProbe,
        recheckEdgeModel,
        availableEdgeModels: AVAILABLE_EDGE_MODELS,
        selectedCameraId,
        setSelectedCameraId,
        activeEventId,
        setActiveEventId,
        activeAlertPayload,
        setActiveAlertPayload
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};
